import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shipmentApi } from '../services/api';
import { fmtDT, fmtDate, STATUS_BADGE, SERVICE_LABELS, addrStr } from '../utils/helpers';
import { Spinner } from '../components/common';
import { Search, CheckCircle, Circle, Package, Phone, MapPin, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';

const DONE_STATUSES = ['DELIVERED','RTO_DELIVERED','CANCELLED','LOST'];

export default function TrackPage() {
  const { trackingId: paramId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tid, setTid]       = useState(paramId || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const pollRef = useRef(null);

  const doTrack = async (id) => {
    const q = (id || tid).trim().toUpperCase();
    if (!q) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await shipmentApi.track(q);
      if (res.success) setResult(res.data);
      else setError('Shipment not found. Please check your tracking ID.');
    } catch { setError('Shipment not found. Please check your tracking ID.'); }
    finally { setLoading(false); }
  };

  // Auto-poll every 60s when OUT_FOR_DELIVERY (CP-FE-033 partial)
  useEffect(() => {
    if (result?.shipment?.status === 'OUT_FOR_DELIVERY') {
      pollRef.current = setInterval(() => doTrack(result.shipment.tracking_id), 60000);
    }
    return () => clearInterval(pollRef.current);
  }, [result?.shipment?.status]); // eslint-disable-line

  // Clear rider location display once delivered (CP-FE-034)
  useEffect(() => {
    if (result?.shipment?.status && DONE_STATUSES.includes(result.shipment.status)) {
      clearInterval(pollRef.current);
    }
  }, [result?.shipment?.status]);

  useEffect(() => { if (paramId) doTrack(paramId); }, [paramId]); // eslint-disable-line

  const s = result?.shipment;
  const statusInfo = s ? (STATUS_BADGE[s.status] || { cls:'badge-gray', label:s.status }) : null;
  const isOutForDelivery = s?.status === 'OUT_FOR_DELIVERY';
  // const isDelivered = s && DONE_STATUSES.includes(s.status);

  // Build full timeline from status history
  const timeline = result?.statusHistory || result?.timeline || [];

  const inner = (
    <div className="track-page" style={user ? { padding: '0 0 40px' } : {}}>
      <div className="track-hero">
        <h1>Track Your Shipment</h1>
        <p>Enter your ECL tracking ID to get real-time updates</p>
        <div className="track-input-row">
          <input
            className="form-control"
            id="tracking-input"
            aria-label="Tracking ID"
            placeholder="e.g. ECLPK202604110000001"
            value={tid}
            onChange={e => setTid(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doTrack()}
            style={{ fontSize:'1rem', padding:'12px 16px' }}
          />
          <button className="btn btn-amber btn-lg" onClick={() => doTrack()} aria-label="Track shipment">
            <Search size={18} /> Track
          </button>
        </div>
      </div>

      <div style={{ maxWidth:720, margin:'32px auto', padding:'0 20px' }}>
        {loading && <Spinner />}

        {/* CP-FE-010: plain language error, no HTTP codes */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <Package size={18} style={{ marginRight:8 }} />
            {error}
          </div>
        )}

        {result && s && (
          <>
            {/* Status card */}
            <div className="card" style={{ marginBottom:20 }}>
              <div className="card-body">
                <div className="flex-between" style={{ flexWrap:'wrap', gap:12 }}>
                  <div>
                    <div style={{ fontSize:'.72rem', color:'var(--gray-400)', marginBottom:4, textTransform:'uppercase', letterSpacing:'.06em' }}>Tracking ID</div>
                    <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:'1.1rem', color:'var(--navy)' }}>{s.tracking_id}</div>
                  </div>
                  <span className={`badge ${statusInfo.cls}`} style={{ fontSize:'.85rem', padding:'6px 16px' }}>
                    {statusInfo.label}
                  </span>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:16, marginTop:20 }}>
                  <InfoBox label="Service"    value={SERVICE_LABELS[s.service_type] || s.service_type} />
                  <InfoBox label="EDD"        value={s.edd ? fmtDate(s.edd) : '—'} />
                  <InfoBox label="Weight"     value={s.weight_actual ? `${s.weight_actual} kg` : '—'} />
                  {s.delivered_at && <InfoBox label="Delivered" value={fmtDT(s.delivered_at)} />}
                  {s.cod_amount > 0 && <InfoBox label="COD Amount" value={`PKR ${Number(s.cod_amount).toLocaleString()}`} />}
                </div>

                {(s.pickup_address || s.delivery_address) && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:20, borderTop:'1px solid var(--gray-100)', paddingTop:16 }}>
                    <InfoBox label="From" value={addrStr(s.pickup_address)} />
                    <InfoBox label="To"   value={addrStr(s.delivery_address)} />
                  </div>
                )}
              </div>
            </div>

            {/* CP-FE-032: Rider info when OUT_FOR_DELIVERY */}
            {isOutForDelivery && (result.riderInfo || s.rider_name) && (
              <div className="card" style={{ marginBottom:20, borderLeft:'4px solid var(--amber)' }}>
                <div className="card-body">
                  <div style={{ fontWeight:600, color:'var(--amber)', marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                    <MapPin size={16} /> Out for Delivery
                    <span style={{ fontSize:'.75rem', fontWeight:400, color:'var(--gray-400)', marginLeft:4 }}>
                      Updates every 60 seconds
                    </span>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                    <div>
                      <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginBottom:2 }}>Delivery Rider</div>
                      <div style={{ fontWeight:600 }}>{result.riderInfo?.name || s.rider_name || '—'}</div>
                    </div>
                    {(result.riderInfo?.mobile || s.rider_mobile) && (
                      <div>
                        <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginBottom:2 }}>Contact</div>
                        <div style={{ fontWeight:500, display:'flex', alignItems:'center', gap:6 }}>
                          <Phone size={12} />
                          <a href={`tel:${result.riderInfo?.mobile || s.rider_mobile}`} style={{ color:'var(--navy)' }}>
                            {result.riderInfo?.mobile || s.rider_mobile}
                          </a>
                        </div>
                      </div>
                    )}
                    {(result.riderInfo?.eta || s.edd) && (
                      <div>
                        <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginBottom:2 }}>Expected By</div>
                        <div style={{ fontWeight:500, display:'flex', alignItems:'center', gap:6 }}>
                          <Clock size={12} /> Today
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Timeline (CP-FE-030, CP-FE-031: all 14 states with timestamps) */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Shipment Timeline</span>
                {isOutForDelivery && (
                  <span style={{ fontSize:'.72rem', color:'var(--amber)', fontWeight:600 }}>🔄 Live updates on</span>
                )}
              </div>
              <div className="card-body">
                {timeline.length ? (
                  <div className="timeline" role="list" aria-label="Shipment status history">
                    {timeline.map((item, i) => {
                      const isLatest = i === timeline.length - 1;
                      const sb = STATUS_BADGE[item.status] || { label: item.status, cls:'badge-gray' };
                      return (
                        <div key={i} className="timeline-item" role="listitem">
                          <div className={`timeline-dot ${isLatest ? 'active' : 'done'}`}>
                            {!isLatest
                              ? <CheckCircle size={10} color="#fff" aria-hidden="true" />
                              : <Circle size={8} color="#fff" aria-hidden="true" />}
                          </div>
                          <div className="timeline-status">
                            {sb.label}
                            {item.hub_name && <span style={{ fontSize:'.75rem', fontWeight:400, color:'var(--gray-500)', marginLeft:8 }}>@ {item.hub_name}</span>}
                            {item.actor_type === 'system' && <span style={{ fontSize:'.68rem', color:'var(--gray-400)', marginLeft:6 }}>(auto)</span>}
                          </div>
                          <div className="timeline-time">{fmtDT(item.created_at)}</div>
                          {item.notes && <div className="timeline-note">{item.notes}</div>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign:'center', padding:24, color:'var(--gray-400)' }}>
                    Status history not available
                  </div>
                )}
              </div>
            </div>

            {/* Shareable link (CP-FE-035) */}
            <div style={{ textAlign:'center', marginTop:16, display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
              <button className="btn btn-outline btn-sm" onClick={() => {
                navigator.clipboard?.writeText(window.location.href);
                alert('Tracking link copied to clipboard!');
              }}>
                🔗 Copy Tracking Link
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => navigate('/login')}>
                Login for full details →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );


  if (user) {
    return <AppShell title="Track Shipment">{inner}</AppShell>;
  }
  return inner;
}

function InfoBox({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:'.7rem', color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:'.875rem', fontWeight:600, color:'var(--gray-800)' }}>{value || '—'}</div>
    </div>
  );
}
